// Utilitaires pour les notifications push
// Compatible avec les apps natives iOS/Android via Capacitor

import { apiClient, IS_NATIVE } from './http';
import { safeLocalStorage } from './safeUtils';

const VAPID_PUBLIC_KEY = 'BP6wDTYaSWwnAkL27YfkCLQkWaRBkuNsdxhb6N_6AZ3aZrEmsU6Boil9yopg2EokS8PyEe1tnr_4x3lPZWvQ-pw';

// Fonction pour convertir la clé VAPID
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Vérifier si les notifications push sont supportées
export function isPushSupported() {
  // Sur native, les notifications push web ne sont pas supportées de la même façon
  if (IS_NATIVE) {
    return false; // On retourne false pour les apps natives (nécessite une config différente)
  }
  return 'serviceWorker' in navigator && 'PushManager' in window;
}

// Demander la permission pour les notifications
export async function requestNotificationPermission() {
  if (!isPushSupported()) {
    console.warn('⚠️ Push notifications ne sont pas supportées');
    return false;
  }

  try {
    const permission = await Notification.requestPermission();
    
    if (permission === 'granted') {
      return true;
    } else if (permission === 'denied') {
      return false;
    } else {
      return false;
    }
  } catch (error) {
    console.error('❌ Erreur demande permission:', error);
    return false;
  }
}

// Enregistrer le service worker
export async function registerServiceWorker() {
  if (!isPushSupported()) {
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register('/service-worker.js');
    
    // Attendre que le service worker soit prêt
    await navigator.serviceWorker.ready;
    
    return registration;
  } catch (error) {
    console.error('❌ Erreur enregistrement service worker:', error);
    return null;
  }
}

// Souscrire aux notifications push
export async function subscribeToPushNotifications(backendUrl) {
  try {
    // Sur native, les notifications push web ne fonctionnent pas
    if (IS_NATIVE) {
      console.warn('⚠️ Push notifications web non supportées sur native');
      return null;
    }

    const registration = await registerServiceWorker();
    if (!registration) {
      console.warn('⚠️ Service worker non disponible');
      return null;
    }

    // Demander la permission
    const hasPermission = await requestNotificationPermission();
    if (!hasPermission) {
      console.warn('⚠️ Permission notifications refusée');
      return null;
    }

    // Vérifier si déjà souscrit
    let subscription = await registration.pushManager.getSubscription();
    
    if (subscription) {
    } else {
      
      try {
        // Créer nouvelle souscription avec gestion d'erreur VAPID
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
        });
        
      } catch (vapidError) {
        // Erreur spécifique VAPID - ne pas bloquer l'utilisateur
        console.warn('⚠️ Clé VAPID invalide ou service worker pas prêt:', vapidError.message);
        return null;
      }
    }

    // Envoyer la souscription au serveur via apiClient
    try {
      const response = await apiClient.post(`${backendUrl}/api/push/subscribe`, subscription.toJSON());

      if (response.status === 200 || response.data) {
        safeLocalStorage.setItem('push-subscription', 'true');
        return subscription;
      } else {
        console.error('❌ Erreur envoi souscription au serveur');
        return null;
      }
    } catch (fetchError) {
      console.error('❌ Erreur envoi souscription:', fetchError);
      return null;
    }

  } catch (error) {
    console.error('❌ Erreur souscription push notifications:', error);
    return null;
  }
}

// Se désabonner des notifications push
export async function unsubscribeFromPushNotifications(backendUrl) {
  try {
    // Sur native, rien à faire
    if (IS_NATIVE) {
      return;
    }

    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) {
      return;
    }

    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      // Désabonner côté client
      await subscription.unsubscribe();

      // Informer le serveur via apiClient
      try {
        await apiClient.post(`${backendUrl}/api/push/unsubscribe`, subscription.toJSON());
      } catch (e) {
        console.warn('⚠️ Erreur désabonnement serveur:', e);
      }

      safeLocalStorage.removeItem('push-subscription');
    }
  } catch (error) {
    console.error('❌ Erreur désabonnement:', error);
  }
}

// Vérifier le statut de la souscription
export async function getPushSubscriptionStatus() {
  try {
    // Sur native, notifications push web non supportées
    if (IS_NATIVE) {
      return { subscribed: false, supported: false, native: true };
    }

    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) {
      return { subscribed: false, supported: false };
    }

    const subscription = await registration.pushManager.getSubscription();
    const permission = Notification.permission;
    
    return {
      supported: isPushSupported(),
      subscribed: !!subscription,
      permission: permission,
      hasLocalStorage: safeLocalStorage.getItem('push-subscription') === 'true'
    };
  } catch (error) {
    console.error('❌ Erreur vérification statut:', error);
    return { subscribed: false, supported: false, error: error.message };
  }
}

// Envoyer une notification de test
export async function sendTestNotification(backendUrl) {
  try {
    // Sur native, pas de test de push web
    if (IS_NATIVE) {
      console.warn('⚠️ Test push non disponible sur native');
      return false;
    }

    const response = await apiClient.post(`${backendUrl}/api/push/test`, {});

    if (response.status === 200 || response.data) {
      return true;
    } else {
      console.error('❌ Erreur envoi notification test');
      return false;
    }
  } catch (error) {
    console.error('❌ Erreur test notification:', error);
    return false;
  }
}
